import { useMemo } from 'react';
import { useReceiptsQuery } from '@gql/schema';
import { buildGroupsAndPeopleFromSelection } from '@secretescapes/cauldron';
import { formatMomentToString } from '@utils/dates.utils';
import { useAppState } from '@utils/useAppState';
import { useApplicationState } from '@utils/useApplicationState';
import { BullfinchFeatureFlags, useFeatureFlag } from '@utils/useFeatureFlag';
import { useServerState } from '@utils/useServerState';
import moment from 'moment';
import { type IFlightState } from '@features/Flights';
import { notEmpty } from './AvailabilityPresentation.utils';
import { type NullableMoment } from './Calendar';
import { useSelectionState } from './SelectionContext';

interface Props {
  checkInDateParam: NullableMoment;
  checkOutDateParam: NullableMoment;
  flightsSelected: boolean;
  flightState: IFlightState;
}

export const useReceipts = ({
  checkInDateParam,
  checkOutDateParam,
  flightsSelected,
  flightState,
}: Props) => {
  const { sale } = useServerState();
  const { saleId } = useAppState();
  const { territoryCode } = useApplicationState();
  const { travellersSelection } = useSelectionState();
  const bookingFeesEnabled = useFeatureFlag(BullfinchFeatureFlags.bookingFeesEnabled);

  const { people, groups } = buildGroupsAndPeopleFromSelection(travellersSelection);
  const { selectedAirport } = flightState;

  const { data, error, loading } = useReceiptsQuery({
    skip: !checkInDateParam || !checkOutDateParam,
    context: {
      headers: {
        ...(!flightsSelected && bookingFeesEnabled
          ? { 'x-ff-tb': BullfinchFeatureFlags.bookingFeesEnabled }
          : {}),
      },
    },
    variables: {
      saleId,
      tracyOfferId: sale?.tracyOfferId ?? '',
      territory: territoryCode,
      date: formatMomentToString(checkInDateParam),
      nights: moment(checkOutDateParam)
        .startOf('d')
        .diff(moment(checkInDateParam).startOf('d'), 'd'),
      people,
      groups,
      departureAirports: flightsSelected && selectedAirport.code ? [selectedAirport.code] : [],
    },
    fetchPolicy: 'no-cache',
  });

  const receipts = useMemo(() => data?.sale?.offers?.filter(notEmpty) ?? [], [data]);

  return {
    offerReceipts: receipts,
    receiptsError: error,
    receiptsLoading: loading,
  };
};
